import React from "react";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {useIntl} from "react-intl";
import {Select, SelectOptionProps, TextField} from "peggirkit";
import {getInputValidationProps} from "@/data/Validation";
import {optionGroupEditValuesFormValidation, OptionGroupType} from "@/data/entities/menuEditor";
import {label} from "@/utils/LabelUtils";
import {OptionGroupFormValues} from "@widgets/menuEditor/optionGroups/edit/optionGroupDraft";

type Props = {
    onSubmit: () => void,
    disabled: boolean,
};

/**
 * Name, type and maximum number of options of an option group.
 */
const OptionGroupFields = ({onSubmit, disabled}: Props) => {
    const intl = useIntl();
    const {control, register, formState: {errors}} = useFormContext<OptionGroupFormValues>();
    const type: OptionGroupType = useWatch({control, name: "type"});
    const typeOptions: SelectOptionProps<string>[] = [
        {value: "optional", displayValue: intl.formatMessage({id: "optional"})},
        {value: "required", displayValue: intl.formatMessage({id: "optionGroupType.required"})},
    ];

    return (
        <form
            className={"w-full grid grid-cols-2 gap-5 md:gap-8"}
            onSubmit={onSubmit}
        >
            <TextField
                containerClassname={"col-span-2"}
                type={"text"}
                label={label("name", true)}
                disabled={disabled}
                error={errors.name && errors.name.message}
                {...register("name", {
                    ...getInputValidationProps(optionGroupEditValuesFormValidation.name, intl),
                })}
            />

            <Controller
                control={control}
                name={"type"}
                render={({field: {value, onChange}}) => (
                    <Select<string>
                        label={label("type", true)}
                        options={typeOptions}
                        value={typeOptions.find(option => option.value === value) ?? null}
                        displayValueEmpty={"-"}
                        onChange={(option: SelectOptionProps<string>) => onChange(option.value as OptionGroupType)}
                        disabled={disabled}
                    />
                )}
            />

            {
                type === "optional" &&
                <TextField
                    type={"number"}
                    min={0}
                    label={label("maxOptions", true)}
                    tip={intl.formatMessage({id: "maxOptions.tip"})}
                    disabled={disabled}
                    error={errors.maxOptions && errors.maxOptions.message}
                    {...register("maxOptions", {
                        ...getInputValidationProps(optionGroupEditValuesFormValidation.maxOptions, intl),
                        valueAsNumber: true,
                    })}
                />
            }
        </form>
    );
};

export default OptionGroupFields;